/*
**  rui-tile.js
**
**  rui-tile component
*/
import { LitElement, html, css } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
export class RuiTile extends LitElement {
    static get is() {
        return 'rui-tile';
    }
    static get properties() {
        return {
            titleText: { type: String },
            iconType: { type: String },
            size: { type: String },
            layout: { type: String },
            appearance: { type: String },
            isDisabled: { type: Object },
            isBusy: { type: Object }
        };
    }
    constructor() {
        super();
        this.titleText = '';
        this.iconType = '';
        this.size = 'wide';
        this.layout = 'float';
        this.appearance = 'default';
        this.isDisabled = false;
        this.isBusy = false;
    }
    click(e) {
        if (!this.isDisabled) {
            let clickEvent = new CustomEvent('rui-click', {
                detail: {},
                bubbles: true,
                composed: true
            });
            this.dispatchEvent(clickEvent);
        }
    }
    render() {
        const size = this.size ? this.size.toLowerCase() : 'wide';
        const appearance = this.appearance ? this.appearance.toLowerCase() : 'default';
        const layout = this.layout ? this.layout.toLowerCase() : 'float';
        let iconContext = 'tile-';
        if (this.isDisabled) {
            iconContext += 'disabled';
        }
        else {
            iconContext += appearance;
        }
        if (size === 'small') {
            iconContext += '-small';
        }
        else {
            iconContext += '-large-wide';
        }
        const buttonClasses = {
            ['rui-tile']: true,
            ['rui-tile-' + size]: true,
            ['rui-tile-layout-fixed']: layout === 'fixed',
            ['rui-tile-appearance-' + appearance]: true
        };
        const containerClasses = {
            ['rui-tile-content']: this.iconType,
            ['rui-tile-content-full-width']: !this.iconType
        };
        return html `
            <button class="${classMap(buttonClasses)}" ?disabled="${this.isDisabled}" type="button" @click="${this.click}">
                <div class="${classMap(containerClasses)}">
                    <div class="rui-tile-header">${this.titleText}</div>
                    <div class="rui-tile-detail">
                        <slot></slot>
                    </div>
                </div>
                ${this.iconType ? html `<rui-icon iconType="${this.iconType}" context="${iconContext}" class="rui-tile-icon" aria-hidden="true"></rui-icon>` : ''}
                ${this.isBusy ? html `<rui-busy-indicator></rui-busy-indicator>` : ''}
            </button>
        `;
    }
    static get styles() {
        return css `
            :host {
                float: left;
            }
            
            :host([layout="fixed"]) {
                float: none;
                width: 100%;
            }
            
            .rui-tile {
                background-color: var(--btn-primary-bg, #3878ab);
                border-style: solid;
                border-width: 1px;
                border-radius: var(--btn-border-radius, 1px);
                box-sizing: border-box;
                cursor: pointer;
                display: inline-block;
                font-family: var(--font-family, "Segoe UI", "Open Sans", "Helvetica Neue", Arial, sans-serif);
                font-size: var(--btn-font-size-md, 14px);
                font-weight: var(--btn-font-weight, 400);
                height: 130px;
                margin-bottom: 10px;
                margin-right: 10px;
                opacity: 0.95;
                padding: 8px;
                position: relative;
                text-align: left;
                user-select: none;
                -webkit-font-smoothing: antialiased;
            }
            
            .rui-tile * {
                box-sizing: border-box;
            }
            
            .rui-tile-icon {
                display: inline-block;
                height: 48px;
                opacity: 0.75;
                position: absolute;
                right: 10px;
                top: 10px;
                width: 48px;
            }
            
            .rui-tile-header {
                font-size: 14pt;
                font-weight: 600;
                line-height: 22px;
                margin: 0 0 10px;
            }
            
            .rui-tile-detail {
                line-height: 20px;
                margin: 0 0 10px;
            }
            
            .rui-tile-content {
                position: absolute;
                left: 10px;
                top: 10px;
                width: calc(100% - 78px);
                height: calc(100% - 20px);
                overflow: hidden;
            }
            
            .rui-tile-content-full-width {
                position: absolute;
                left: 10px;
                top: 10px;
                width: calc(100% - 20px);
                height: calc(100% - 20px);
                overflow: hidden;
            }
            
            .rui-tile-small .rui-tile-content-full-width {
                text-align: center;
                top: 20px;
            }
            
            .rui-tile-small {
                width: 130px;
                height: 130px;
            }
            
            .rui-tile-small .rui-tile-icon {
                right: calc(50% - 14px);
                top: 11px;
                width: 28px;
                height: 28px;
                font-size: 28px;
            }
            
            .rui-tile-small .rui-tile-header {
                font-size: 12pt;
                line-height: 18px;
            }
            
            .rui-tile-small .rui-tile-content {
                height: calc(100% - 65px);
                left: 0;
                position: relative;
                text-align: center;
                top: 15px;
                width: 100%;
            }
            
            a.rui-tile-small .rui-tile-content {
                top: 48px;
            }
            
            .rui-tile-large
            {
                width: 270px;
                height: 270px;
            }
            
            .rui-tile-wide
            {
                width: 270px;
                height: 130px;
            }
            
            .rui-tile-layout-fixed {
                width: 100%;
            }
            
            .rui-tile-appearance-default {
                background-color: var(--btn-primary-bg, #3878ab);
                border-color: var(--btn-primary-bg, #3878ab);
                color: var(--btn-primary-fg, #fff);
            }
            
            .rui-tile-appearance-default:hover {
                background-color: var(--btn-primary-bg-hover, #2e628c);
                border-color: var(--btn-primary-bg-hover, #2e628c);
                color: var(--btn-primary-fg-hover, #fff);
            }
            
            .rui-tile-appearance-default:focus,
            .rui-tile-appearance-default:active {
                background-color: var(--btn-primary-bg-hover, #2e628c);
                border-color: var(--btn-primary-bg-hover, #2e628c);
                color: var(--btn-primary-fg-hover, #fff);
                outline: var(--outline-width, 1px) var(--outline-style, solid) var(--btn-primary-border, #3878ab);
                outline-offset: var(--outline-offset-btn, 0);
            }
            
            .rui-tile-appearance-highlighted,
            .rui-tile-appearance-highlighted:visited 
            {
                background-color: var(--tile-highlighted-bg, #fa0);
                border-color: var(--tile-highlighted-bg, #fa0);
                color: var(--tile-highlighted-fg, #333);
            }
            
            .rui-tile-appearance-highlighted:hover {
                background-color: var(--tile-highlighted-bg-hover, #ffcc66);
                border-color: var(--tile-highlighted-bg-hover, #ffcc66);
                color: var(--tile-highlighted-fg, #333);
            }
            
            .rui-tile-appearance-highlighted:focus,
            .rui-tile-appearance-highlighted:active {
                background-color: var(--tile-highlighted-bg-hover, #ffcc66);
                border-color: var(--tile-highlighted-bg-hover, #ffcc66);
                color: var(--tile-highlighted-fg, #333);
                outline: var(--outline-width, 1px) var(--outline-style, solid) var(--btn-primary-border, #3878ab);
                outline-offset: var(--outline-offset-btn, 0);
            }
            
            .rui-tile-appearance-alternate1,
            .rui-tile-appearance-alternate1:visited {
                background-color: var(--tile-alt1-bg, #186FB3);
                border-color: var(--tile-alt1-bg, #186FB3);
                color: var(--tile-alt1-fg, #fff);
            }
            
            .rui-tile-appearance-alternate1:hover {
                background-color: var(--tile-alt1-bg-hover, #4aa2e7);
                border-color: var(--tile-alt1-bg-hover, #4aa2e7);
                color: var(--tile-alt1-fg, #fff);
            }
            
            .rui-tile-appearance-alternate1:focus,
            .rui-tile-appearance-alternate1:active {
                background-color: var(--tile-alt1-bg-hover, #4aa2e7);
                border-color: var(--tile-alt1-bg-hover, #4aa2e7);
                color: var(--tile-alt1-fg, #fff);
                outline: var(--outline-width, 1px) var(--outline-style, solid) var(--btn-primary-border, #3878ab);
                outline-offset: var(--outline-offset-btn, 0);
            }
            
            .rui-tile-appearance-alternate2,
            .rui-tile-appearance-alternate2:visited {
                background-color: var(--tile-alt2-bg, #104773);
                border-color: var(--tile-alt2-bg, #104773);
                color: var(--tile-alt2-fg, #fff);
            }
            
            .rui-tile-appearance-alternate2:hover {
                background-color: var(--tile-alt2-bg-hover, #1c7ecd);
                border-color: var(--tile-alt2-bg-hover, #1c7ecd);
                color: var(--tile-alt2-fg, #fff);
            }
            
            .rui-tile-appearance-alternate2:focus,
            .rui-tile-appearance-alternate2:active {
                background-color: var(--tile-alt2-bg-hover, #1c7ecd);
                border-color: var(--tile-alt2-bg-hover, #1c7ecd);
                color: var(--tile-alt2-fg, #fff);
                outline: var(--outline-width, 1px) var(--outline-style, solid) var(--btn-primary-border, #3878ab);
                outline-offset: var(--outline-offset-btn, 0);
            }
            
            .rui-tile-appearance-alternate3, 
            rui-tile-appearance-alternate3:visited {
                background-color: var(--tile-alt3-bg, #072034);
                border-color: var(--tile-alt3-bg, #072034);
                color: var(--tile-alt3-fg, #fff);
            }
            
            .rui-tile-appearance-alternate3:hover {
                background-color: var(--tile-alt3-bg-hover, #13578e);
                border-color: var(--tile-alt3-bg-hover, #13578e);
                color: var(--tile-alt3-fg, #fff);
            }
            
            .rui-tile-appearance-alternate3:focus,
            .rui-tile-appearance-alternate3:active {
                background-color: var(--tile-alt3-bg-hover, #13578e);
                border-color: var(--tile-alt3-bg-hover, #13578e);
                color: var(--tile-alt3-fg, #fff);
                outline: var(--outline-width, 1px) var(--outline-style, solid) var(--btn-primary-border, #3878ab);
                outline-offset: var(--outline-offset-btn, 0);
            }
            
            .rui-tile-appearance-error, 
            rui-tile-appearance-error:visited {
                background-color: var(--btn-destructive-bg, #fff2f2);
                border-color: var(--btn-destructive-border, #e13329);
                color: var(--btn-destructive-fg, #e13329);
            }
            
            .rui-tile-appearance-error:hover {
                background-color: var(--btn-destructive-bg-hover, #f9e5e5);
                border-color: var(--btn-destructive-border, #e13329);
                color: var(--btn-destructive-fg-hover, #e13329);
            }
            
            .rui-tile-appearance-error:focus,
            .rui-tile-appearance-error:active {
                background-color: var(--btn-destructive-bg-hover, #f9e5e5);
                border-color: var(--btn-destructive-border, #e13329);
                color: var(--btn-destructive-fg-hover, #e13329);
                outline: var(--outline-width, 1px) var(--outline-style, solid) var(--btn-destructive-border, #e13329);
                outline-offset: var(--outline-offset-btn, 0);
            }
            
            .rui-tile:disabled {
                cursor: not-allowed;
                filter: none;
                background-color: var(--btn-primary-bg-disabled, #e9e9e9);
                border-color: var(--btn-primary-bg-disabled, #e9e9e9);
                color: var(--btn-primary-fg-disabled, #999);
            }
            
            @media  #{$phone} {
                .rui-tile,
                .rui-tile:visited {
                    margin-left: 5px;
                    margin-right: 5px;
                    width: calc(100% - 10px);
                }
            
                .rui-tile-small, .rui-tile-small:visited {
                    width: calc(50% - 10px);
                }
            }
            
            @media print {
                .rui-tile {
                    background-color: transparent;
                    border-color: #000000;
                    color: #000000;
                }
            
                .rui-tile-header {
                    color: #000000;
                }
            
                .rui-tile-detail:not(:disabled) {
                    color: #000000;
                }
            
                .rui-tile:disabled {
                    opacity: 0.66;
                    background-color: #cccccc;
                    border-color: #666666;
                    color: #666666;
                }
            }
        `;
    }
}
customElements.get(RuiTile.is) || customElements.define(RuiTile.is, RuiTile);
